import { useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import VerificationPageLayout from '../../components/Layout/VerificationPageLayout';
import SchemaForm, { FormField } from '../../components/Forms/SchemaForm';
import VerificationResultDisplay from '../../components/Verification/VerificationResultDisplay';
import { accountVerificationService, avsSchema } from '../../services/verificationSystemServices/accountVerificationService';

const AccountVerificationService = () => {
    type verificationType = 'Individual' | 'Company';
    type entityType = 'None' | 'RegisteredCompany' | 'SoleProp' | 'Trust';
    const Verification_Type_INDIVIDUAL: verificationType = 'Individual';
    const Verification_Type_COMPANY: verificationType = 'Company';
    const Verification_EntityType_NONE: entityType = 'None';
    const Verification_EntityType_REGISTERED_COMPANY: entityType = 'RegisteredCompany';
    const Verification_EntityType_SOLE_PROP: entityType = 'SoleProp';
    const Verification_EntityType_TRUST: entityType = 'Trust';

    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<any | null>(null);

    const formFields: FormField[] = [
        {
            name: 'verificationType',
            label: 'Verification Type',
            type: 'select',
            required: true,
            options: [
                { label: 'Individual', value: Verification_Type_INDIVIDUAL },
                { label: 'Company', value: Verification_Type_COMPANY },
            ],
            gridProps: { xs: 12, md: 6 },
            confirmReset: true,
        },
        {
            name: 'entityType',
            label: 'Entity Type',
            type: 'select',
            required: true,
            options: [
                { label: 'None', value: Verification_EntityType_NONE },
                { label: 'Registered Company', value: Verification_EntityType_REGISTERED_COMPANY },
                { label: 'Sole Proprietor', value: Verification_EntityType_SOLE_PROP },
                { label: 'Trust', value: Verification_EntityType_TRUST },
            ],
            gridProps: { xs: 12, md: 6 },
        },

        // Individual Fields
        {
            name: 'initials',
            label: 'Initials',
            type: 'text',
            gridProps: { xs: 12, md: 6 },
            required: (values) => values.verificationType === Verification_Type_INDIVIDUAL,
            visibleIf: (values) => values.verificationType === Verification_Type_INDIVIDUAL,
        },
        {
            name: 'surname',
            label: 'Surname',
            type: 'text',
            gridProps: { xs: 12, md: 6 },
            required: (values) => values.verificationType === Verification_Type_INDIVIDUAL,
            visibleIf: (values) => values.verificationType === Verification_Type_INDIVIDUAL,
        },
        {
            name: 'idNo',
            label: 'ID Number',
            type: 'text',
            gridProps: { xs: 12, md: 6 },
            required: (values) => values.verificationType === Verification_Type_INDIVIDUAL,
            visibleIf: (values) => values.verificationType === Verification_Type_INDIVIDUAL,
            maxLength: 13,
        },
        {
            name: 'idType',
            label: 'ID Type',
            type: 'select',
            options: [
                { label: 'SA ID (SID)', value: 'SID' },
                { label: 'SA Passport (SPP)', value: 'SPP' },
                { label: 'Foreign Passport (FPP)', value: 'FPP' },
            ],
            gridProps: { xs: 12, md: 6 },
            required: (values) => values.verificationType === Verification_Type_INDIVIDUAL,
            visibleIf: (values) => values.verificationType === Verification_Type_INDIVIDUAL,
        },

        // Company Fields
        {
            name: 'companyName',
            label: 'Company Name',
            type: 'text',
            gridProps: { xs: 12, md: 12 },
            required: (values) => values.verificationType === Verification_Type_COMPANY,
            visibleIf: (values) => values.verificationType === Verification_Type_COMPANY,
        },
        {
            name: 'reg1',
            label: 'Reg1 (YYYY)',
            type: 'text',
            gridProps: { xs: 12, md: 4 },
            required: (values) => values.verificationType === Verification_Type_COMPANY,
            visibleIf: (values) => values.verificationType === Verification_Type_COMPANY,
        },
        {
            name: 'reg2',
            label: 'Reg2 (NNNNNN)',
            type: 'text',
            gridProps: { xs: 12, md: 4 },
            required: (values) => values.verificationType === Verification_Type_COMPANY,
            visibleIf: (values) => values.verificationType === Verification_Type_COMPANY,
        },
        {
            name: 'reg3',
            label: 'Reg3 (NN)',
            type: 'text',
            gridProps: { xs: 12, md: 4 },
            required: (values) => values.verificationType === Verification_Type_COMPANY,
            visibleIf: (values) => values.verificationType === Verification_Type_COMPANY,
        },
        {
            name: 'trustNumber',
            label: 'Trust Number',
            type: 'text',
            gridProps: { xs: 12, md: 6 },
            required: false,
            visibleIf: (values) =>
                values.verificationType === Verification_Type_COMPANY &&
                values.entityType === Verification_EntityType_TRUST,
        },

        // Common Fields
        {
            name: 'accountNumber',
            label: 'Account Number',
            type: 'text',
            required: true,
            gridProps: { xs: 12, md: 6 },
        },
        {
            name: 'branchCode',
            label: 'Branch Code',
            type: 'text',
            required: true,
            gridProps: { xs: 12, md: 6 },
        },
        {
            name: 'accountType',
            label: 'Account Type',
            type: 'select',
            required: true,
            options: [
                { label: 'Cheque / Current Account', value: 'CURRENTSCHECKACCOUNT' },
                { label: 'Savings Account', value: 'SAVINGSACCOUNT' },
                { label: 'Transmission Account', value: 'TRANSMISSION' },
                { label: 'Bond Account', value: 'BOND' },
                { label: 'Subscription Share Account', value: 'SUBSCRIPTIONSHARE' },
            ],
            gridProps: { xs: 12, md: 6 },
        },
        {
            name: 'bankName',
            label: 'Bank Name',
            type: 'select',
            required: true,
            options: [
                { label: 'FNB', value: 'FNB' },
                { label: 'Standard Bank', value: 'STANDARDBANK' },
                { label: 'ABSA', value: 'ABSA' },
                { label: 'Nedbank', value: 'NEDBANK' },
                { label: 'Capitec', value: 'CAPITEC' },
                { label: 'African Bank', value: 'AFRICANBANK' },
                { label: 'Investec', value: 'INVESTEC' },
                { label: 'Bidvest Bank', value: 'BIDVESTBANK' },
                { label: 'Discovery', value: 'DISCOVERY' },
                { label: 'Grindrod', value: 'GRINDROD' },
                { label: 'Sasfin', value: 'SASFIN' },
            ],
            gridProps: { xs: 12, md: 6 },
        },
        {
            name: 'voucherCode',
            label: 'Voucher Code',
            type: 'text',
            gridProps: { xs: 12, md: 6 },
            required: false,
        },
        {
            name: 'clientReference',
            label: 'Client Reference',
            type: 'text',
            gridProps: { xs: 12, md: 6 },
            required: false,
        },
    ];

    const handleSubmit = async (data: any) => {
        setLoading(true);
        setResult(null);

        try {
            const payload = accountVerificationService.createPayload(data);
            const response = await accountVerificationService.submitAVS(payload);
            setResult(response);
        } catch (err: any) {
            const detail = err?.response?.data?.detail || err?.message || 'An unexpected error occurred.';
            setResult({ success: false, message: detail, avs_data: null });
        } finally {
            setLoading(false);
        }
    };

    return (
        <VerificationPageLayout title="Account Verification Service">
            <SchemaForm
                schema={formFields}
                onSubmit={handleSubmit}
                defaultValues={{
                    verificationType: 'Individual',
                    entityType: 'None',
                }}
                validationSchema={avsSchema}
                submitLabel={loading ? 'Verifying…' : 'Verify Account'}
            />

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress sx={{ color: '#2D4677' }} />
                </Box>
            )}

            {!loading && result && (
                <VerificationResultDisplay
                    title="Account Verification Service"
                    requestId={result.request_id}
                    success={result.success}
                    message={result.message}
                    data={result.avs_data ?? null}
                />
            )}
        </VerificationPageLayout>
    );
};

export default AccountVerificationService;
